import React, { useState, useEffect } from 'react';
import { FaFilter, FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';
import Select from 'react-select';
import { useAuth } from '../../contexts/AuthContext';
import { getAllMorphologies, getAllInstabilities } from '../../services/metadataService';

const DatasetFilterPanel = ({ onFilterChange, initialFilters = {}, disabled = false, showStatusFilter = false }) => {
    const { isAuthenticated } = useAuth();
    const [isOpen, setIsOpen] = useState(false);

    // Metadata Options
    const [morphologyOptions, setMorphologyOptions] = useState([]);
    const [instabilityOptions, setInstabilityOptions] = useState([]);

    // Default empty filters
    const defaultFilters = {
        doi: '',
        polymer: '',
        solvent: '',
        collectorType: '',
        needleType: '',
        morphology: [], // Changed to array for multi-select
        instability: [], // Changed to array for multi-select
        isFormationStable: '',
        polymerType: '',
        solutionPreparation: '',
        onlyMyData: false,
        concentrationMin: '', concentrationMax: '',
        needleDiameterMin: '', needleDiameterMax: '',
        voltageMin: '', voltageMax: '',
        flowRateMin: '', flowRateMax: '',
        tipDistMin: '', tipDistMax: '',
        tempMin: '', tempMax: '',
        humidityMin: '', humidityMax: '',
        fiberDiameterMin: '', fiberDiameterMax: '',
        variationMin: '', variationMax: '',
        weightMin: '', weightMax: '',

        // Mechanical
        tensileStrengthMin: '', tensileStrengthMax: '',
        modulusMin: '', modulusMax: '',
        elongationAtBreakMin: '', elongationAtBreakMax: '',
        fractureBehaviour: '',

        // Functional
        surfaceAreaMin: '', surfaceAreaMax: '',
        porosityMin: '', porosityMax: '',
        permeabilityMin: '', permeabilityMax: '',
        wettabilityMin: '', wettabilityMax: '',
        electricalConductivityMin: '', electricalConductivityMax: '',
        thermalConductivityMin: '', thermalConductivityMax: '',
        status: ''
    };

    const [filters, setFilters] = useState({ ...defaultFilters, ...initialFilters });

    useEffect(() => {
        // Fetch Metadata Options
        const fetchOptions = async () => {
            try {
                const [morphRes, instRes] = await Promise.all([
                    getAllMorphologies(),
                    getAllInstabilities()
                ]);

                if (morphRes) {
                    setMorphologyOptions(morphRes.map(d => ({ value: d.label, label: d.label })));
                }
                if (instRes) {
                    setInstabilityOptions(instRes.map(d => ({ value: d.label, label: d.label })));
                }
            } catch (err) {
                console.error("Failed to fetch filter options", err);
            }
        };
        fetchOptions();
    }, []);

    // Update local state when initialFilters prop changes (e.g. loading a version)
    useEffect(() => {
        if (initialFilters && Object.keys(initialFilters).length > 0) {
            setFilters(prev => ({ ...prev, ...initialFilters }));
        } else {
            setFilters(prev => ({ ...defaultFilters, ...initialFilters }));
        }
    }, [initialFilters]);

    const handleChange = (e) => {
        if (disabled) return;
        const { name, value, type, checked } = e.target;
        const newFilters = { ...filters, [name]: type === 'checkbox' ? checked : value };
        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    const handleMultiSelectChange = (selectedOptions, { name }) => {
        if (disabled) return;
        // selectedOptions is array of { value, label } or null
        const values = selectedOptions ? selectedOptions.map(o => o.value) : [];
        const newFilters = { ...filters, [name]: values };
        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    const handleClear = () => {
        if (disabled) return;
        setFilters(defaultFilters);
        onFilterChange(defaultFilters);
    };

    const toggleOpen = () => setIsOpen(!isOpen);

    const renderRangeInput = (label, minName, maxName, unit = '') => (
        <div className="col-md-3 col-sm-6 mb-3">
            <label className="form-label small fw-bold text-muted mb-1">{label} {unit && `(${unit})`}</label>
            <div className="input-group input-group-sm">
                <input
                    type="number"
                    className="form-control"
                    placeholder="Min"
                    name={minName}
                    value={filters[minName] || ''}
                    onChange={handleChange}
                    disabled={disabled}
                />
                <span className="input-group-text bg-light">-</span>
                <input
                    type="number"
                    className="form-control"
                    placeholder="Max"
                    name={maxName}
                    value={filters[maxName] || ''}
                    onChange={handleChange}
                    disabled={disabled}
                />
            </div>
        </div>
    );

    const renderTextInput = (label, name, placeholder = 'Contains...') => (
        <div className="col-md-3 col-sm-6 mb-3">
            <label className="form-label small fw-bold text-muted mb-1">{label}</label>
            <input
                type="text"
                className="form-control form-control-sm"
                placeholder={placeholder}
                name={name}
                value={filters[name] || ''}
                onChange={handleChange}
                disabled={disabled}
            />
        </div>
    );

    const renderMultiSelect = (label, name, options) => {
        // Convert current filter value (array of strings) back to options objects for React Select
        const currentValues = filters[name] || [];
        const selectedValue = options.filter(o => currentValues.includes(o.value));

        return (
            <div className="col-md-3 col-sm-6 mb-3">
                <label className="form-label small fw-bold text-muted mb-1">{label}</label>
                <Select
                    name={name}
                    options={options}
                    value={selectedValue}
                    onChange={handleMultiSelectChange}
                    isMulti
                    isDisabled={disabled}
                    placeholder="Select..."
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={{
                        control: (base) => ({ ...base, minHeight: '31px', fontSize: '0.875rem' }),
                        valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                        input: (base) => ({ ...base, margin: 0, padding: 0 }),
                    }}
                />
            </div>
        );
    };

    return (
        <div className={`card border-0 shadow-sm mb-4 ${disabled ? 'opacity-75' : ''}`}>
            <div
                className="card-header bg-white border-bottom-0 py-3 d-flex justify-content-between align-items-center cursor-pointer"
                onClick={toggleOpen}
                style={{ cursor: 'pointer' }}
            >
                <div className="d-flex align-items-center text-primary">
                    <FaFilter className="me-2" />
                    <h6 className="mb-0 fw-bold">Advanced Filters & Versioning {disabled && '(Read-Only)'}</h6>
                </div>
                <div>
                    {(Object.keys(filters).some(key => {
                        if (Array.isArray(filters[key])) return filters[key].length > 0;
                        return filters[key] !== defaultFilters[key];
                    })) && (
                            <span className="badge bg-warning text-dark me-3">Filters Active</span>
                        )}
                    {isOpen ? <FaChevronUp className="text-muted" /> : <FaChevronDown className="text-muted" />}
                </div>
            </div>

            {isOpen && (
                <div className="card-body border-top bg-light bg-opacity-10">
                    <div className="row">
                        {/* Text Filters */}
                        {renderTextInput('DOI', 'doi')}
                        {renderTextInput('Polymer Name', 'polymer')}
                        {renderTextInput('Solvent Name', 'solvent')}
                        {renderTextInput('Collector Type', 'collectorType')}
                        {renderTextInput('Needle Type', 'needleType')}
                        {renderTextInput('Fracture Behaviour', 'fractureBehaviour')}

                        {/* Multi-Select Filters */}
                        {renderMultiSelect('Morphology', 'morphology', morphologyOptions)}
                        {renderMultiSelect('Instability', 'instability', instabilityOptions)}

                        {showStatusFilter && (
                            <div className="col-md-3 col-sm-6 mb-3">
                                <label className="form-label small fw-bold text-muted mb-1">Status</label>
                                <select
                                    className="form-select form-select-sm"
                                    name="status"
                                    value={filters.status || ''}
                                    onChange={handleChange}
                                    disabled={disabled}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Rejected">Rejected</option>
                                    <option value="Pending">Pending</option>
                                </select>
                            </div>
                        )}

                        {/* Copolymer / blend / core-shell, and how the solution was prepared */}
                        <div className="col-md-3 col-sm-6 mb-3">
                            <label className="form-label small fw-bold text-muted mb-1" htmlFor="filter-polymer-type">Polymer Type</label>
                            <select id="filter-polymer-type" className="form-select form-select-sm" name="polymerType"
                                value={filters.polymerType || ''} onChange={handleChange} disabled={disabled}>
                                <option value="">All</option>
                                <option value="Homopolymer">Homopolymer</option>
                                <option value="Copolymer">Copolymer</option>
                                <option value="Blend">Blend</option>
                                <option value="Core/shell">Core/shell (coaxial, triaxial)</option>
                            </select>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                            <label className="form-label small fw-bold text-muted mb-1" htmlFor="filter-solution-prep">Solution Preparation</label>
                            <select id="filter-solution-prep" className="form-select form-select-sm" name="solutionPreparation"
                                value={filters.solutionPreparation || ''} onChange={handleChange} disabled={disabled}>
                                <option value="">All</option>
                                <option value="Single solution">Single solution</option>
                                <option value="Mixed solutions">Mixed from separate solutions</option>
                                <option value="Core/shell solutions">Core/shell solutions</option>
                            </select>
                        </div>

                        {/* Boolean Filter */}
                        <div className="col-md-3 col-sm-6 mb-3">
                            <label className="form-label small fw-bold text-muted mb-1">Formation Stable?</label>
                            <select
                                className="form-select form-select-sm"
                                name="isFormationStable"
                                value={filters.isFormationStable || ''}
                                onChange={handleChange}
                                disabled={disabled}
                            >
                                <option value="">All</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>

                        <div className="col-md-3 col-sm-6 mb-3 d-flex align-items-end">
                            {isAuthenticated && (
                                <div className="form-check mb-1">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name="onlyMyData"
                                        id="onlyMyDataCheckbox"
                                        checked={filters.onlyMyData || false}
                                        onChange={handleChange}
                                        disabled={disabled}
                                    />
                                    <label className="form-check-label small fw-bold text-muted" htmlFor="onlyMyDataCheckbox">
                                        Show Only My Data
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Range Filters */}
                        {renderRangeInput('Concentration', 'concentrationMin', 'concentrationMax')}
                        {renderRangeInput('Needle Diameter', 'needleDiameterMin', 'needleDiameterMax', 'G')}
                        {renderRangeInput('Voltage', 'voltageMin', 'voltageMax', 'kV')}
                        {renderRangeInput('Flow Rate', 'flowRateMin', 'flowRateMax', 'mL/h')}
                        {renderRangeInput('Tip Distance', 'tipDistMin', 'tipDistMax', 'cm')}
                        {renderRangeInput('Temperature', 'tempMin', 'tempMax', '°C')}
                        {renderRangeInput('Humidity', 'humidityMin', 'humidityMax', '%')}
                        {renderRangeInput('Fiber Diameter', 'fiberDiameterMin', 'fiberDiameterMax', 'nm')}
                        {renderRangeInput('Diam. Variation', 'variationMin', 'variationMax')}
                        {renderRangeInput('Product Weight', 'weightMin', 'weightMax')}

                        <div className="col-12 mt-3 mb-2 border-bottom">
                            <h6 className="fw-bold text-primary small">Mechanical Properties</h6>
                        </div>
                        {renderRangeInput('Tensile Str.', 'tensileStrengthMin', 'tensileStrengthMax', 'MPa')}
                        {renderRangeInput('Modulus', 'modulusMin', 'modulusMax', 'MPa')}
                        {renderRangeInput('Elongation', 'elongationAtBreakMin', 'elongationAtBreakMax', '%')}

                        <div className="col-12 mt-3 mb-2 border-bottom">
                            <h6 className="fw-bold text-primary small">Functional Properties</h6>
                        </div>
                        {renderRangeInput('Surface Area', 'surfaceAreaMin', 'surfaceAreaMax', 'm²/g')}
                        {renderRangeInput('Porosity', 'porosityMin', 'porosityMax', '%')}
                        {renderRangeInput('Permeability', 'permeabilityMin', 'permeabilityMax')}
                        {renderRangeInput('Wettability', 'wettabilityMin', 'wettabilityMax', '°')}
                        {renderRangeInput('Electrical Cond.', 'electricalConductivityMin', 'electricalConductivityMax')}
                        {renderRangeInput('Thermal Cond.', 'thermalConductivityMin', 'thermalConductivityMax')}
                    </div>

                    {!disabled && (
                        <div className="d-flex justify-content-end mt-2 pt-2 border-top">
                            <button
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                                onClick={handleClear}
                            >
                                <FaTimes className="me-1" /> Clear All Filters
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DatasetFilterPanel;
